import { joinUrl, setWindowTitle, trimSlashes } from '@repo/utils';
import { getAppConfig } from '@/utils';
import { computed, watch, ref } from 'vue';
import { useParamStore } from './param';
import type { MetadataProperty } from '@repo/style3d';
import { useRelationStore } from '@repo/stores';
import { defineStore } from '@repo/stores';
import type { FabricStore } from '@repo/style3d';

interface GroupedData {
  [key: string]: {
    name: string;
    value: string;
  }[];
}

const { OSS_BASE_URL, INDIVIDUAL_SHARES, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const baseUrl = `${OSS_BASE_URL}${INDIVIDUAL_SHARES}`;
const baseFabricUrl = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

export const useShareStore = defineStore('share', () => {

  const paramStore = useParamStore();

  const relationStore = useRelationStore();

  const property = computed(() => {
    const groupedData = relationStore.relation?.metadata?.property.reduce((acc: GroupedData, property: MetadataProperty) => {
      const title = property.title || 'default';
      if (!acc[title]) {
        acc[title] = [];
      }
      acc[title] = acc[title]!.concat(property.data);
      return acc;
    }, {});

    // 暂时未做分组
    return groupedData?.default;
  });

  const currentPathId = computed(() => relationStore.relation?.pathId || '');

  const prevLinkUri = computed(() => relationStore.relation?.linkUri?.prevLinkUri.map((item) => ({
    uri: trimSlashes(item.uri),
    thumb: joinUrl(baseUrl, item.thumb),
    fileName: item.fileName
  })) || []);
  const nextLinkUri = computed(() => relationStore.relation?.linkUri?.nextLinkUri.map((item) => ({
    uri: trimSlashes(item.uri),
    thumb: joinUrl(baseUrl, item.thumb),
    fileName: item.fileName
  })) || []);

  // 缓存已请求过的 fabric 名称，避免重复请求同一个info.json，性能优化关键
  const fabricNameCache = new Map<string, string>();

  const splitToFabricStore = async (str: string): Promise<FabricStore> => {
    const prefix:string = "https://oss.cloudknit.cn/fabric/";
    const id:string = str.startsWith(prefix) ? str.slice(prefix.length) : str;
    const thumb:string = joinUrl(baseFabricUrl, id, "fabric", "thumbnail.jpg");
    const infoUrl:string = joinUrl(baseFabricUrl, id, "info.json");

    // 优先从缓存读取，无缓存则发起请求
    let name = '';
    if (fabricNameCache.has(id)) {
        name = fabricNameCache.get(id)!;
    } else {
        try {
            // 发起get请求获取info.json数据
            const res = await fetch(infoUrl);
            const data = await res.json();
            // 精准取值：匹配你给的json嵌套格式 metaData -> info -> name
            name = data?.metaData?.info?.name || '';
            // 存入缓存，后续同id的面料无需重复请求
            fabricNameCache.set(id, name);
        } catch (error) {
            // 异常兜底：网络失败/文件不存在/格式错误时，name为空字符串
            console.warn(`面料${id}的info.json请求失败`, error);
            name = '';
        }
    }

    return {
      id,
      thumb,
      name
    };
  };

  const fabricList = ref<FabricStore[]>([]);

  const loadFabricList = async () => {
    const fabricLinks = relationStore.relation?.metadata?.fabricLinks || [];
    const arr = await Promise.all(fabricLinks.map(str => splitToFabricStore(str)));
    fabricList.value = arr;
  };

  watch(() => relationStore.relation?.metadata?.fabricLinks, () => {
    void loadFabricList();
  }, { immediate: true });

  const carouselList = computed(() => {
    if (!relationStore.relation?.linkUri) {
      return [];
    }
    if (!prevLinkUri.value.length && !nextLinkUri.value.length) {
      return [];
    }
    const curr = relationStore.relation?.linkUri.curUri;
    return [...prevLinkUri.value, {
      uri: trimSlashes(curr!.uri),
      thumb: joinUrl(baseUrl, curr!.thumb),
      fileName: curr!.fileName
    }, ...nextLinkUri.value];
  });

  const nextPathId = computed(() => {
    return nextLinkUri.value[0]?.uri;
  });

  const prevPathId = computed(() => {
    return prevLinkUri.value[0]?.uri;
  });

  const logoUrl = computed(() => relationStore.relation?.metadata?.logo && joinUrl(baseUrl, paramStore.scoId, relationStore.relation?.metadata.logo));
  const enterpriseName = computed(() => relationStore.relation?.metadata?.enterpriseName);
  const styleThumb = computed(() => {
    const pictureFileName = relationStore.relation?.metadata?.stylePicture || "正面图.png";
    const primaryUrl = paramStore.scoId?encodeURI(joinUrl(baseUrl, paramStore.scoId, "img", pictureFileName)):'';
    const fallbackUrl = paramStore.scoId?encodeURI(joinUrl(baseUrl, paramStore.scoId, "sco", "thumb.png")):'';
    return {primaryUrl, fallbackUrl};
  });
  const styleName = computed(() => {
    const propertyList = relationStore.relation?.metadata?.property;
    if (!propertyList) return '';
    const propertyItem = propertyList[0];
    const find = propertyItem?.data.find(item => item.key === 'styleName');
    return find?find.value:'';
});
  return {
    currentPathId,
    property,
    carouselList,
    nextPathId,
    prevPathId,
    logoUrl,
    enterpriseName,
    fabricList,
    styleThumb,
    styleName
  };
});
